// useDashboardMetrics — owns the dashboard's metric data via a single
// GET /dashboard/summary query: permission gating, per-area status mapping
// and currency formatting for the hero band KPIs and stat blocks.
//
// Data-confidence contract: aggregation happens server-side over FULLY
// drained lists and the endpoint fails closed (500, never partial numbers).
// Client-side, an area's values are defined only when the query succeeded AND
// the response carries that area's key; while loading or after any failure
// the values are undefined and `status` says so — consumers render an
// explicit placeholder, never a partial or approximate number. AUD compact
// formatting stays here: the wire carries the raw portfolio number.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { dashboardApi } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { formatAudCompact } from "../utils/formatCurrency";
import type { ContractListItem } from "../types";

export type DashboardMetricsStatus = "loading" | "success" | "error";

export interface GenerateDashboardMetrics {
  status: DashboardMetricsStatus;
  totalContracts?: number;
  draftCount?: number;
  generatedCount?: number;
  totalSeries?: number[];
  draftSeries?: number[];
  generatedSeries?: number[];
  recentDrafts?: ContractListItem[];
  recentGenerated?: ContractListItem[];
}

export interface ManageDashboardMetrics {
  status: DashboardMetricsStatus;
  contractCount?: number;
  activeCount?: number;
  supplierCount?: number;
  portfolioValueFormatted?: string;
  expiringWithin90Days?: number;
  newLast12WeeksCount?: number;
  newLast12WeeksSeries?: number[];
}

function queryStatus(query: {
  isSuccess: boolean;
  isError: boolean;
}): DashboardMetricsStatus {
  if (query.isError) return "error";
  if (query.isSuccess) return "success";
  return "loading";
}

export function useDashboardMetrics(): {
  generate: GenerateDashboardMetrics;
  manage: ManageDashboardMetrics;
} {
  const { hasArea } = useAuth();

  const summaryQuery = useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: () => dashboardApi.summary(),
    enabled: hasArea("generate") || hasArea("manage"),
  });

  const generate = useMemo<GenerateDashboardMetrics>(() => {
    const status = queryStatus(summaryQuery);
    const summary = summaryQuery.data?.generate;
    // Values only when the query succeeded AND the area key is present —
    // an absent key means the area is not held, so its values stay undefined.
    if (status !== "success" || !summary) return { status };

    return { status, ...summary };
  }, [summaryQuery]);

  const manage = useMemo<ManageDashboardMetrics>(() => {
    const status = queryStatus(summaryQuery);
    const summary = summaryQuery.data?.manage;
    if (status !== "success" || !summary) return { status };

    const { portfolioValue, ...counts } = summary;
    return {
      status,
      ...counts,
      portfolioValueFormatted: formatAudCompact(portfolioValue),
    };
  }, [summaryQuery]);

  return { generate, manage };
}
